import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { cn } from '../lib/cn';
import { rangeTime } from '../lib/format';
import { statusMeta, type TStatusGroup } from '../status/dictionary';

export interface IChartBucket {
  start: string;
  end: string;
  total: number;
  byStatus: Record<string, number>;
}

const BANDS = ['success', 'failure', 'motion', 'suppressed'] as const;
export type TBand = (typeof BANDS)[number];

// Keys of `byStatus` that are not event statuses (run groups) name their own band and words.
export type TBarSeries = Readonly<Record<string, { band: TBand; label: string }>>;

const BAND_FILL: Record<TBand, string> = {
  success: 'var(--color-success)',
  failure: 'var(--color-danger)',
  motion: 'var(--color-motion)',
  suppressed: 'var(--color-suppressed)',
};

const GROUP_BAND: Record<TStatusGroup, TBand> = {
  success: 'success',
  failure: 'failure',
  motion: 'motion',
  retrying: 'motion',
  suppressed: 'suppressed',
  indeterminate: 'suppressed',
};

const bandOf = (status: string, series?: TBarSeries): TBand => {
  const own = series?.[status];
  if (own) return own.band;
  const meta = statusMeta('event', status);
  return meta ? GROUP_BAND[meta.group] : 'suppressed';
};

const labelOf = (status: string, series?: TBarSeries): string =>
  series?.[status]?.label ?? statusMeta('event', status)?.label ?? status;

const bandsOf = (byStatus: Record<string, number>, series?: TBarSeries): Record<TBand, number> => {
  const out: Record<TBand, number> = { success: 0, failure: 0, motion: 0, suppressed: 0 };
  for (const [status, count] of Object.entries(byStatus)) out[bandOf(status, series)] += count;
  return out;
};

export interface IBucketRange {
  from: number;
  to: number;
}

interface IStatusBarsProps {
  buckets: readonly IChartBucket[];
  label: string;
  tall?: boolean;
  selection?: IBucketRange | null;
  onSelect?: (range: IBucketRange) => void;
  timeZone?: string;
  now: number;
  className?: string;
  series?: TBarSeries;
  /** What one count is, plural: "events" unless said. */
  unit?: string;
}

const GAP = 2;
const BASELINE = 1;
const TOOLTIP_WIDTH = 236;
const TOOLTIP_GAP = 16;
const DRAG_THRESHOLD_PX = 4;

const Tooltip = ({
  bucket,
  panelRef,
  timeZone,
  now,
  series,
  unit,
}: {
  bucket: IChartBucket;
  panelRef: React.RefObject<HTMLDivElement | null>;
  timeZone?: string;
  now: number;
  series?: TBarSeries;
  unit: string;
}) => {
  const rows = Object.entries(bucket.byStatus)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div
      ref={panelRef}
      className="pointer-events-none absolute top-0 left-0 z-10 w-tooltip rounded-panel border border-border-raised bg-tooltip p-3 shadow-panel"
    >
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 font-mono text-meta text-primary">
          {rangeTime(bucket.start, bucket.end, now, timeZone)}
        </span>
        <span className="ml-auto shrink-0 font-mono text-micro text-faint">
          {bucket.total} {unit}
        </span>
      </div>

      <div className="mt-2 flex flex-col gap-label">
        {rows.map(([status, count]) => (
          <div key={status} className="flex items-center gap-2">
            <span
              aria-hidden
              className="size-dot shrink-0 rounded-full"
              style={{ background: BAND_FILL[bandOf(status, series)] }}
            />
            <span className="truncate text-meta text-muted">{labelOf(status, series)}</span>
            <span className="ml-auto font-mono text-meta text-primary">{count}</span>
          </div>
        ))}
      </div>

      <p className="mt-2 text-meta text-faint">Click to keep this window · drag for a longer one</p>
    </div>
  );
};

export const StatusBars = ({
  buckets,
  label,
  tall,
  selection,
  onSelect,
  timeZone,
  now,
  className,
  series,
  unit = 'events',
}: IStatusBarsProps) => {
  const ref = useRef<SVGSVGElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const pointRef = useRef<{ x: number; y: number } | null>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<number | null>(null);
  const [drag, setDrag] = useState<{ anchor: number; head: number; startX: number } | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const count = buckets.length;
  const height = tall ? 120 : 88;
  const step = count > 0 ? width / count : 0;

  const indexAt = useCallback(
    (clientX: number) => {
      const box = ref.current?.getBoundingClientRect();
      if (!box || step === 0) return 0;
      return Math.min(count - 1, Math.max(0, Math.floor((clientX - box.left) / step)));
    },
    [count, step],
  );

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!onSelect || event.button !== 0) return;
    const index = indexAt(event.clientX);
    // Without capture, a release outside the chart never arrives and the selection stays stuck to the pointer.
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({ anchor: index, head: index, startX: event.clientX });
  };

  const placePanel = (clientX: number, clientY: number) => {
    const panel = panelRef.current;
    const box = ref.current?.getBoundingClientRect();
    if (!panel || !box) return;

    const x = clientX - box.left;
    const y = clientY - box.top;
    const flip = x + TOOLTIP_GAP + TOOLTIP_WIDTH > box.width;
    const left = flip ? x - TOOLTIP_GAP - TOOLTIP_WIDTH : x + TOOLTIP_GAP;
    const top = y - panel.offsetHeight / 2;

    panel.style.transform = `translate3d(${Math.max(0, left)}px, ${top}px, 0)`;
  };

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    const index = indexAt(event.clientX);
    pointRef.current = { x: event.clientX, y: event.clientY };
    setHover(index);
    placePanel(event.clientX, event.clientY);
    if (drag) setDrag({ ...drag, head: index });
  };

  const onPointerUp = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!drag || !onSelect) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    const dragged = Math.abs(event.clientX - drag.startX) >= DRAG_THRESHOLD_PX;
    setDrag(null);
    onSelect(
      dragged
        ? { from: Math.min(drag.anchor, drag.head), to: Math.max(drag.anchor, drag.head) }
        : { from: drag.anchor, to: drag.anchor },
    );
  };

  // Runs on every render on purpose: the panel does not exist yet on the move that first reveals it.
  useLayoutEffect(() => {
    const point = pointRef.current;
    if (hover !== null && point) placePanel(point.x, point.y);
  });

  const live = drag
    ? { from: Math.min(drag.anchor, drag.head), to: Math.max(drag.anchor, drag.head) }
    : selection;

  const peak = Math.max(1, ...buckets.map((bucket) => bucket.total));
  const barWidth = Math.max(1, step - GAP);
  const hovered = hover !== null ? buckets[hover] : undefined;

  return (
    <div className={cn('relative', className)}>
      <svg
        ref={ref}
        role="img"
        aria-label={label}
        width="100%"
        height={height}
        viewBox={`0 0 ${Math.max(width, 1)} ${height}`}
        className={cn('block w-full select-none', onSelect && 'cursor-crosshair')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setDrag(null)}
        onPointerLeave={() => setHover(null)}
      >
        {width > 0 &&
          buckets.map((bucket, index) => {
            const x = index * step;
            const dimmed = live != null && (index < live.from || index > live.to);
            const opacity = dimmed ? 0.35 : 1;
            const column =
              index === hover ? (
                <rect x={x} y={0} width={barWidth} height={height} fill="var(--color-hover)" />
              ) : null;

            // An empty bucket draws a baseline rect, never nothing: "we looked and found none" must not look like "we never asked".
            if (bucket.total === 0) {
              return (
                <g key={bucket.start}>
                  {column}
                  <rect
                    x={x}
                    y={height - BASELINE}
                    width={barWidth}
                    height={BASELINE}
                    fill="var(--color-border-sunken)"
                    opacity={opacity}
                  />
                </g>
              );
            }

            const bands = bandsOf(bucket.byStatus, series);
            let y = height;

            return (
              <g key={bucket.start}>
                {column}
                {BANDS.map((band) => {
                  const value = bands[band];
                  if (value === 0) return null;
                  const bandHeight = Math.max(1, (value / peak) * (height - BASELINE));
                  y -= bandHeight;
                  return (
                    <rect
                      key={band}
                      x={x}
                      y={y}
                      width={barWidth}
                      height={bandHeight}
                      fill={BAND_FILL[band]}
                      opacity={opacity}
                    />
                  );
                })}
              </g>
            );
          })}

        {live && width > 0 && (
          <g>
            <rect
              x={live.from * step}
              y={0}
              width={Math.max(barWidth, (live.to - live.from + 1) * step - GAP)}
              height={height}
              fill="var(--color-primary)"
              opacity={0.1}
            />
            <rect x={live.from * step} y={0} width={1} height={height} fill="var(--color-accent)" />
            <rect
              x={Math.max(0, (live.to + 1) * step - GAP - 1)}
              y={0}
              width={1}
              height={height}
              fill="var(--color-accent)"
            />
          </g>
        )}
      </svg>

      {hovered && !drag && (
        <Tooltip
          bucket={hovered}
          panelRef={panelRef}
          timeZone={timeZone}
          now={now}
          series={series}
          unit={unit}
        />
      )}

      {live && drag && buckets[live.from] && buckets[live.to] && (
        <div className="pointer-events-none absolute -top-8 left-0 flex items-center gap-3 font-mono text-micro text-primary">
          <span>{rangeTime(buckets[live.from].start, buckets[live.to].end, now, timeZone)}</span>
          <span className="text-faint">
            {buckets.slice(live.from, live.to + 1).reduce((sum, bucket) => sum + bucket.total, 0)}{' '}
            {unit}
          </span>
        </div>
      )}
    </div>
  );
};
